// src/pages/enterprise-ai/MoneyPage.js
// WALLET-LOADS-PANEL-PAGE-01. Every wallet load, newest first, with the two money-in
// actions staff used to run as org-backend scripts:
//   "Cancel" on a pending Stripe bank transfer (initiateBankTransferLoad.mjs --cancel)
//   "Record a check or wire"                    (settleManualLoad.mjs)
//   "Start a bank transfer"                     (initiateBankTransferLoad.mjs, PANEL-ORG-AND-LOADS-UI-01 C)
//
// No United money: the only button that credits a wallet is "Record a check or wire",
// for money that already arrived (bank reference and received date required). Starting
// a bank transfer creates a pending load only.
//
// The panel never moves money. Every call goes to the panel backend's /api/money/*
// proxies (routes/moneyWalletLoadsRoutes.js), which forward to org-backend's internal
// wallet-loads doors with the staff email as `by`. Every write is two steps: a dry run
// whose answer is shown as the preview, then a confirm that sends the SAME body with
// dryRun false. Every refusal renders as one line: CODE: message (refusalLine).

import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, MenuItem, Button, CircularProgress, Alert, Autocomplete, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { T6_BASE, authHeader } from '../enterprise/EnterpriseAgentAccessTab';
import { refusalLine } from '../enterprise/AddEnterpriseDialog';
import { THEME } from './genesisData';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const STATUSES = ['pending', 'processing', 'available', 'failed', 'cancelled'];
const METHODS = [
    { value: 'check', label: 'Check' },
    { value: 'wire', label: 'Wire' },
    { value: 'ach', label: 'ACH' },
];

// A Stripe row is one org-backend created from a PaymentIntent (org-backend
// services/walletLoadQueryService.js isStripeRow).
const isStripeRow = (r) => /^pi_/.test(r.stripePaymentIntentId || '');

export function formatMoney(cents, currency) {
    if (!Number.isInteger(cents)) return 'unknown';
    const code = String(currency || 'USD').toUpperCase();
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + code;
    }
}

// Exact decimal string to integer cents, the rule org-backend's settleManualLoad.mjs
// parseAmountToCents applies. null when the string is not dollars with at most two
// decimals, or not above zero.
export function parseAmountToCents(input) {
    const s = String(input == null ? '' : input).trim().replace(/,/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
    const [whole, frac = ''] = s.split('.');
    const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
    return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

function methodLabel(r) {
    if (r.source === 'stripe_card') return 'Card';
    const rail = r.source === 'ach' ? 'ACH' : r.source === 'wire' ? 'Wire' : r.source === 'check' ? 'Check' : (r.source || 'unknown');
    return isStripeRow(r) ? 'Bank transfer (' + rail + ')' : rail;
}

const STATUS_COLOR = {
    available: [THEME.mint, THEME.mintDim],
    pending: [THEME.amber, THEME.amberDim],
    processing: [THEME.blue, THEME.blueDim],
    failed: [THEME.pink, THEME.cardRaised],
    cancelled: [THEME.textMuted, THEME.cardRaised],
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const today = () => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};

const staffEmail = () => {
    try { return getAuth().currentUser?.email || null; } catch (e) { return null; }
};

async function money(method, path, { params, body } = {}) {
    const headers = await authHeader();
    const res = await axios.request({ method, url: `${T6_BASE}/api/money${path}`, params, data: body, headers });
    return res.data;
}

// ─────────────────────────────────────────────
// STYLED
// ─────────────────────────────────────────────

const PageContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem',
    backgroundColor: THEME.bg,
    minHeight: '100vh',
});

const Cell = styled(TableCell)({
    padding: '12px 14px',
    fontSize: '0.85rem',
    color: THEME.textPrimary,
    borderBottom: `1px solid ${THEME.border}`,
});

const HeadCell = styled(Cell)({
    color: THEME.mint,
    fontWeight: 600,
});

const fieldSx = {
    InputLabelProps: { sx: { color: THEME.textMuted } },
    InputProps: { sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } },
};

const primaryBtnSx = {
    bgcolor: THEME.mint, color: '#000', fontWeight: 700, textTransform: 'none',
    '&:hover': { bgcolor: THEME.mint },
    '&:disabled': { bgcolor: THEME.cardRaised, color: THEME.textMuted },
};

const dangerBtnSx = {
    bgcolor: THEME.pink, color: THEME.textPrimary, fontWeight: 700, textTransform: 'none',
    '&:hover': { bgcolor: THEME.pink },
    '&:disabled': { bgcolor: THEME.cardRaised, color: THEME.textMuted },
};

const dialogPaper = { sx: { bgcolor: THEME.bg, color: THEME.textPrimary, borderRadius: '12px' } };

function RefusalLine({ text }) {
    if (!text) return null;
    return <Alert severity="error" sx={{ mb: 2, bgcolor: THEME.cardRaised, color: THEME.pink }}>{text}</Alert>;
}

function Row({ label, value, mono }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
            <Typography sx={{ color: THEME.textMuted, fontSize: '0.85rem' }}>{label}</Typography>
            <Typography sx={{ color: THEME.textPrimary, fontSize: '0.85rem', textAlign: 'right', wordBreak: 'break-all', ...(mono ? { fontFamily: 'monospace' } : {}) }}>
                {value}
            </Typography>
        </Box>
    );
}

// ─────────────────────────────────────────────
// CANCEL (a pending Stripe bank transfer)
// ─────────────────────────────────────────────

function CancelLoadDialog({ load, onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const staff = staffEmail();

    useEffect(() => {
        if (!load) return undefined;
        let live = true;
        setPreview(null);
        setError(null);
        setBusy(true);
        money('POST', '/wallet-loads/' + encodeURIComponent(load.loadId) + '/cancel', { body: { dryRun: true } })
            .then((data) => { if (live) setPreview(data); })
            .catch((err) => { if (live) setError(refusalLine(err)); })
            .finally(() => { if (live) setBusy(false); });
        return () => { live = false; };
    }, [load]);

    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            await money('POST', '/wallet-loads/' + encodeURIComponent(load.loadId) + '/cancel', { body: { dryRun: false } });
            onDone();
        } catch (err) {
            setError(refusalLine(err));
            setBusy(false);
        }
    };

    if (!load) return null;
    const amount = formatMoney(preview && Number.isInteger(preview.amountCents) ? preview.amountCents : load.amountCents, load.currency);
    const ready = preview && preview.dryRun === true && preview.wouldCancel === true;

    return (
        <Dialog open={!!load} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
            <DialogTitle sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, fontWeight: 700 }}>Cancel a pending bank transfer</DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mt: 2 }}>
                    <RefusalLine text={error} />
                    {busy && !preview && !error && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                            <CircularProgress size={18} sx={{ color: THEME.mint }} />
                            <Typography sx={{ color: THEME.textSecondary }}>Checking the load with org-backend (dry run, nothing changes)...</Typography>
                        </Box>
                    )}
                    {ready && (
                        <>
                            <Typography sx={{ color: THEME.textPrimary, mb: 2 }}>
                                This cancels the pending bank transfer of {amount} for {load.orgName || load.orgId}. If money arrives later for it, it will be refused and returned.
                            </Typography>
                            <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 }}>
                                <Row label="Load" value={preview.loadId} mono />
                                <Row label="Load status" value={preview.loadStatus} />
                                <Row label="Funding" value={preview.funding} />
                                <Row label="Stripe mode" value={preview.mode || '—'} />
                                <Row label="Cancelled by" value={staff || 'your staff session'} />
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Keep it</Button>
                <Button onClick={confirm} disabled={!ready || busy} variant="contained" sx={dangerBtnSx}
                    startIcon={busy && preview ? <CircularProgress size={16} /> : null}>
                    {busy && preview ? 'Cancelling...' : 'Confirm cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─────────────────────────────────────────────
// RECORD A CHECK OR WIRE
// ─────────────────────────────────────────────

const EMPTY_DEPOSIT = { org: null, amount: '', currency: '', source: 'check', reference: '', receivedAt: today() };

function RecordDepositDialog({ open, orgs, walletsByOrg, walletsUnread, onClose, onCredited }) {
    const [form, setForm] = useState(EMPTY_DEPOSIT);
    const [preview, setPreview] = useState(null);
    const [previewBody, setPreviewBody] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const staff = staffEmail();

    const reset = () => { setForm({ ...EMPTY_DEPOSIT, receivedAt: today() }); setPreview(null); setPreviewBody(null); setResult(null); setError(null); };
    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };

    // Any edit after a preview discards it: the confirm only ever sends what was previewed.
    const edit = (patch) => { setForm((f) => ({ ...f, ...patch })); setPreview(null); setPreviewBody(null); setError(null); };

    const pickOrg = (org) => {
        const w = org ? walletsByOrg[String(org._id)] : null;
        edit({ org, currency: w && w.currency ? w.currency : '' });
    };

    const amountCents = parseAmountToCents(form.amount);
    const orgWallet = form.org ? walletsByOrg[String(form.org._id)] : null;
    const formValid = !!form.org && amountCents !== null && /^[A-Za-z]{3}$/.test(form.currency.trim())
        && form.reference.trim() && /^\d{4}-\d{2}-\d{2}$/.test(form.receivedAt);

    const runPreview = async () => {
        const body = {
            orgId: String(form.org._id),
            amountCents,
            currency: form.currency.trim().toUpperCase(),
            source: form.source,
            reference: form.reference.trim(),
            receivedAt: form.receivedAt,
        };
        setBusy(true);
        setError(null);
        try {
            const data = await money('POST', '/wallet-loads/manual-settle', { body: { ...body, dryRun: true } });
            setPreview(data);
            setPreviewBody(body);
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            const data = await money('POST', '/wallet-loads/manual-settle', { body: { ...previewBody, dryRun: false } });
            setResult(data);
            onCredited();
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    const orgName = form.org ? (form.org.name || form.org.legalName || String(form.org._id)) : '';
    const actionLine = (p) => {
        if (p.action === 'resume') return 'Finishes an earlier recording of this reference that stopped before crediting.';
        if (p.action === 'adopt') return 'Settles the open bank load ' + p.loadId + ' that the customer started in the app.';
        return 'Records a new load for this deposit.';
    };

    const renderPreview = () => {
        if (preview.duplicate) {
            return (
                <Alert severity="warning" sx={{ bgcolor: THEME.amberDim, color: THEME.textPrimary }}>
                    MANUAL_LOAD_DUPLICATE: reference {previewBody.reference} is already credited on load {preview.loadId}. Nothing will be credited again.
                </Alert>
            );
        }
        return (
            <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 }}>
                <Typography sx={{ color: THEME.mint, fontWeight: 600, mb: 1 }}>
                    {formatMoney(previewBody.amountCents, preview.currency)} will be credited to {orgName}
                </Typography>
                <Row label="Wallet" value={preview.walletId || 'created on confirm (the organisation has none yet)'} mono={!!preview.walletId} />
                <Row label="Balance now" value={formatMoney(preview.beforeCents, preview.currency)} />
                <Row label="Balance after" value={formatMoney(preview.afterCents, preview.currency)} />
                <Row label="Method" value={METHODS.find((m) => m.value === previewBody.source)?.label} />
                <Row label="Bank reference" value={previewBody.reference} mono />
                <Row label="Received" value={previewBody.receivedAt} />
                <Row label="Recorded by" value={staff || 'your staff session'} />
                <Typography sx={{ color: THEME.textSecondary, fontSize: '0.8rem', mt: 1 }}>{actionLine(preview)}</Typography>
            </Box>
        );
    };

    const renderResult = () => (
        <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 }}>
            {result.duplicate ? (
                <Typography sx={{ color: THEME.amber, fontWeight: 600 }}>Already credited on load {result.loadId}; nothing changed.</Typography>
            ) : (
                <>
                    <Typography sx={{ color: THEME.mint, fontWeight: 600, mb: 1 }}>
                        {formatMoney(previewBody.amountCents, result.currency)} credited to {orgName}
                    </Typography>
                    <Row label="Load" value={result.loadId} mono />
                    <Row label="Wallet" value={result.walletId} mono />
                    <Row label="Balance now" value={formatMoney(result.afterCents, result.currency)} />
                </>
            )}
        </Box>
    );

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
            <DialogTitle sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, fontWeight: 700 }}>Record a check or wire</DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mt: 2 }}>
                    {result ? renderResult() : (
                        <>
                            <Typography sx={{ color: THEME.textSecondary, fontSize: '0.9rem', mb: 2 }}>
                                For money that landed in United's bank account. Preview first; nothing is credited until you confirm.
                            </Typography>
                            <RefusalLine text={error} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Autocomplete
                                        options={orgs}
                                        value={form.org}
                                        onChange={(e, v) => pickOrg(v)}
                                        getOptionLabel={(o) => o.name || o.legalName || String(o._id)}
                                        isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Organisation *"
                                                InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                                                InputProps={{ ...params.InputProps, sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } }} />
                                        )}
                                    />
                                    {form.org && !orgWallet && !walletsUnread && (
                                        <Typography sx={{ color: THEME.amber, fontSize: '0.75rem', mt: 0.5 }}>This organisation has no wallet on record; type the currency.</Typography>
                                    )}
                                    {walletsUnread && (
                                        <Typography sx={{ color: THEME.amber, fontSize: '0.75rem', mt: 0.5 }}>{walletsUnread}</Typography>
                                    )}
                                </Box>
                                <TextField label="Amount *" value={form.amount} onChange={(e) => edit({ amount: e.target.value })}
                                    placeholder="25000.00" error={!!form.amount && amountCents === null}
                                    helperText={form.amount && amountCents === null ? 'Dollars with at most two decimals' : ' '} {...fieldSx} />
                                <TextField label="Currency *" value={form.currency} onChange={(e) => edit({ currency: e.target.value })}
                                    inputProps={{ maxLength: 3 }} disabled={!!(orgWallet && orgWallet.currency)}
                                    helperText={orgWallet && orgWallet.currency ? "The organisation's wallet currency" : ' '}
                                    FormHelperTextProps={{ sx: { color: THEME.textMuted } }} {...fieldSx} />
                                <TextField select label="Method *" value={form.source} onChange={(e) => edit({ source: e.target.value })} {...fieldSx}>
                                    {METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                </TextField>
                                <TextField label="Date received *" type="date" value={form.receivedAt} onChange={(e) => edit({ receivedAt: e.target.value })}
                                    InputLabelProps={{ shrink: true, sx: { color: THEME.textMuted } }} InputProps={fieldSx.InputProps} />
                                <TextField sx={{ gridColumn: '1 / -1' }} label="Bank reference or check number *" value={form.reference}
                                    onChange={(e) => edit({ reference: e.target.value })} {...fieldSx} />
                            </Box>
                            {preview && renderPreview()}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                {result ? (
                    <Button onClick={close} sx={{ color: THEME.mint, textTransform: 'none', fontWeight: 600 }}>Done</Button>
                ) : (
                    <>
                        <Button onClick={close} disabled={busy} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Close</Button>
                        {preview && !preview.duplicate ? (
                            <Button onClick={confirm} disabled={busy} variant="contained" sx={primaryBtnSx}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Crediting...' : 'Confirm and credit'}
                            </Button>
                        ) : (
                            <Button onClick={runPreview} disabled={!formValid || busy || !!preview} variant="contained" sx={primaryBtnSx}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Checking...' : 'Preview'}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

// ─────────────────────────────────────────────
// START A BANK TRANSFER (PANEL-ORG-AND-LOADS-UI-01 C)
// Creates a PENDING load at org-backend and shows Stripe's funding instructions for
// staff to send the customer. Nothing is credited here: the wallet goes up when the
// customer's transfer arrives and Stripe's webhook settles the load on org-backend.
// An open pending load for the same organisation and amount is shown first, with its
// instructions, so a second one is never made by accident (standing rule 38).
// ─────────────────────────────────────────────

// The customer line comes from org-backend (`customerLine`, one string from its config
// with Stripe's fee named; INTERNAL-BANK-TRANSFER-START-ROUTE-01). This constant is only
// the fallback when an answer lacks it, and using it is logged (Rule 3a).
export const BANK_TRANSFER_ARRIVAL_LINE = 'Include the reference code on the transfer. The wallet is credited when the money arrives, usually 1 to 3 business days for a wire or ACH.';

function lineOrFallback(customerLine, loadId) {
    if (customerLine) return customerLine;
    console.warn('[PANEL_BANK_TRANSFER_LINE_FALLBACK] ' + JSON.stringify({
        loadId: loadId || null,
        consequence: "org-backend's answer carried no customerLine; the panel's own line is shown and it does not name Stripe's fee",
    }));
    return BANK_TRANSFER_ARRIVAL_LINE;
}

// Whether org-backend has the bank transfer routes yet. available: true, false, or
// null when the probe itself failed (the dialog then tries and shows the refusal).
export function useBankTransferAvailability() {
    const [state, setState] = useState({ loading: true, available: null, message: null });
    useEffect(() => {
        let live = true;
        money('GET', '/bank-transfer/availability')
            .then((d) => { if (live) setState({ loading: false, available: !!d.available, message: d.message || null }); })
            .catch((err) => {
                console.error('[Money] bank transfer availability unread', err.response?.status, err.response?.data || err.message);
                if (live) setState({ loading: false, available: null, message: null });
            });
        return () => { live = false; };
    }, []);
    return state;
}

function instructionsText(orgName, i, amountCents, line) {
    const lines = ['Bank transfer instructions for ' + orgName, ''];
    lines.push('Amount: ' + formatMoney(Number.isInteger(i.amountCents) ? i.amountCents : amountCents, i.currency || 'USD'));
    lines.push('Bank name: ' + (i.bankName || 'not given'));
    lines.push('Routing number: ' + (i.routingNumber || 'not given'));
    lines.push('Account number: ' + (i.accountNumber || 'not given'));
    if (i.accountHolderName) lines.push('Account holder: ' + i.accountHolderName);
    lines.push('Reference code: ' + (i.reference || 'not given'));
    lines.push('', line);
    return lines.join('\n');
}

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        // Older browsers and a page without clipboard permission: the textarea route.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (e2) { ok = false; }
        document.body.removeChild(ta);
        return ok;
    }
}

function InstructionsBox({ orgName, instructions, amountCents, loadId, customerLine }) {
    const [copied, setCopied] = useState(null);
    // Decided once per load, so the fallback is logged once, not on every render.
    const line = useMemo(
        () => (instructions ? lineOrFallback(customerLine, loadId) : null),
        [instructions, customerLine, loadId],
    );
    if (!instructions) {
        return (
            <Alert severity="info" sx={{ bgcolor: THEME.cardRaised, color: THEME.textPrimary }}>
                Stripe returned no transfer instructions for load {loadId}: the PaymentIntent is not waiting for a transfer, so there is nothing to send.
            </Alert>
        );
    }
    const i = instructions;
    const copy = async () => {
        const ok = await copyText(instructionsText(orgName, i, amountCents, line));
        setCopied(ok ? 'Copied. Paste it into your email to the customer.' : 'COPY_FAILED: the browser refused the clipboard; select the lines above and copy them by hand.');
    };
    return (
        <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 }}>
            <Typography sx={{ color: THEME.mint, fontWeight: 600, mb: 1 }}>Bank transfer instructions for {orgName}</Typography>
            <Row label="Amount" value={formatMoney(Number.isInteger(i.amountCents) ? i.amountCents : amountCents, i.currency || 'USD')} />
            <Row label="Bank name" value={i.bankName || 'not given'} />
            <Row label="Routing number" value={i.routingNumber || 'not given'} mono />
            <Row label="Account number" value={i.accountNumber || 'not given'} mono />
            {i.accountHolderName && <Row label="Account holder" value={i.accountHolderName} />}
            <Row label="Reference code" value={i.reference || 'not given'} mono />
            {loadId && <Row label="Load" value={loadId} mono />}
            <Typography sx={{ color: THEME.textSecondary, fontSize: '0.85rem', mt: 1.5 }}>{line}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" onClick={copy} variant="outlined"
                    sx={{ color: THEME.mint, borderColor: THEME.mint, textTransform: 'none', fontWeight: 600 }}>
                    Copy for email
                </Button>
                {copied && <Typography sx={{ color: /^COPY_FAILED/.test(copied) ? THEME.pink : THEME.textMuted, fontSize: '0.8rem' }}>{copied}</Typography>}
            </Box>
        </Box>
    );
}

const EMPTY_TRANSFER = { org: null, amount: '' };

// `availability` is the page's useBankTransferAvailability(), so the probe runs once.
export function StartBankTransferDialog({ open, orgs, initialOrg, availability, onClose, onCreated }) {
    const [walletsByOrg, setWalletsByOrg] = useState({});
    const [form, setForm] = useState(EMPTY_TRANSFER);
    const [preview, setPreview] = useState(null);
    const [previewBody, setPreviewBody] = useState(null);
    const [existing, setExisting] = useState(null);
    const [existingInstr, setExistingInstr] = useState(null);
    const [existingLine, setExistingLine] = useState(null);
    const [result, setResult] = useState(null);
    const [resultInstr, setResultInstr] = useState(null);
    const [resultLine, setResultLine] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setForm({ ...EMPTY_TRANSFER, org: initialOrg || null });
        money('GET', '/org-wallets')
            .then((data) => {
                const by = {};
                for (const w of data.wallets || []) if (w.orgId && !by[w.orgId]) by[w.orgId] = w;
                setWalletsByOrg(by);
            })
            .catch((err) => console.error('[Money] org wallets unread', err.response?.status, err.response?.data || err.message));
    }, [open, initialOrg]);

    const clearPreview = () => { setPreview(null); setPreviewBody(null); setExisting(null); setExistingInstr(null); setExistingLine(null); setError(null); };
    const reset = () => { setForm(EMPTY_TRANSFER); clearPreview(); setResult(null); setResultInstr(null); setResultLine(null); };
    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };
    // Any edit after a preview discards it: Create only ever sends what was previewed.
    const edit = (patch) => { setForm((f) => ({ ...f, ...patch })); clearPreview(); };

    const amountCents = parseAmountToCents(form.amount);
    const orgWallet = form.org ? walletsByOrg[String(form.org._id)] : null;
    const currency = String((orgWallet && orgWallet.currency) || 'USD').toUpperCase();
    // Stripe's bank transfer on org-backend is us_bank_transfer, USD only.
    const currencyRefusal = currency !== 'USD'
        ? 'BANK_TRANSFER_CURRENCY_UNSUPPORTED: Stripe bank transfers are in USD only and this organisation\'s wallet is in ' + currency + '.'
        : null;
    const orgName = form.org ? (form.org.name || form.org.legalName || String(form.org._id)) : '';
    const formValid = !!form.org && amountCents !== null && !currencyRefusal;
    const blocked = availability.available === false;

    // { instructions, customerLine } for a load, from org-backend's instructions route.
    const readInstructions = async (loadId) => {
        const d = await money('GET', '/bank-transfer/' + encodeURIComponent(loadId) + '/instructions');
        return { instructions: d.instructions || null, customerLine: d.customerLine || null };
    };
    const NO_INSTRUCTIONS = { instructions: null, customerLine: null };

    // org-backend answers an open load as existingPending: true with the load on the top
    // level; a row from the loads list is the load itself.
    const openLoadFrom = (data, match) => {
        if (data.existingPending === true) {
            const i = data.instructions || null;
            return {
                loadId: data.loadId, amountCents: (i && i.amountCents) ?? amountCents, currency: (i && i.currency) || currency,
                createdAt: data.createdAt || null, instructions: i, customerLine: data.customerLine || null,
            };
        }
        if (data.existingPending && typeof data.existingPending === 'object') return data.existingPending;
        return match;
    };

    const runPreview = async () => {
        const body = { orgId: String(form.org._id), amountCents };
        setBusy(true);
        setError(null);
        try {
            // An open pending Stripe bank transfer for this organisation and amount, from
            // the loads list, beside whatever the dry run itself reports.
            const list = await money('GET', '/wallet-loads', { params: { orgId: body.orgId, status: 'pending', limit: 100 } });
            const match = (list.loads || []).find((l) => isStripeRow(l) && l.source !== 'stripe_card' && l.amountCents === amountCents) || null;
            const data = await money('POST', '/bank-transfer', { body: { ...body, dryRun: true } });
            const openLoad = openLoadFrom(data, match);
            setPreview(data);
            setPreviewBody(body);
            setExisting(openLoad);
            if (openLoad) {
                const read = openLoad.instructions
                    ? { instructions: openLoad.instructions, customerLine: openLoad.customerLine || data.customerLine || null }
                    : await readInstructions(openLoad.loadId).catch((err) => { setError(refusalLine(err)); return NO_INSTRUCTIONS; });
                setExistingInstr(read.instructions);
                setExistingLine(read.customerLine);
            }
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    const create = async () => {
        setBusy(true);
        setError(null);
        try {
            const data = await money('POST', '/bank-transfer', { body: { ...previewBody, dryRun: false } });
            setResult(data);
            let read = { instructions: data.instructions || null, customerLine: data.customerLine || null };
            if (!read.instructions && data.loadId) {
                const again = await readInstructions(data.loadId).catch((err) => { setError(refusalLine(err)); return NO_INSTRUCTIONS; });
                read = { instructions: again.instructions, customerLine: read.customerLine || again.customerLine };
            }
            setResultInstr(read.instructions);
            setResultLine(read.customerLine);
            onCreated && onCreated();
        } catch (err) {
            setError(refusalLine(err));
        } finally {
            setBusy(false);
        }
    };

    const amountText = amountCents !== null ? formatMoney(amountCents, currency) : '';

    const renderExisting = () => (
        <>
            <Alert severity="warning" sx={{ bgcolor: THEME.amberDim, color: THEME.textPrimary, mb: 2 }}>
                A pending bank transfer of {formatMoney(existing.amountCents ?? amountCents, existing.currency || currency)} for {orgName} is already open
                {existing.createdAt ? ' (started ' + fmtDate(existing.createdAt) + ')' : ''}. Send these instructions instead of starting another one.
                If a new one is really needed, cancel this one on the Money page first.
            </Alert>
            <InstructionsBox orgName={orgName} instructions={existingInstr} amountCents={existing.amountCents ?? amountCents}
                loadId={existing.loadId} customerLine={existingLine} />
        </>
    );

    const renderResult = () => {
        const ep = result.existingPending;
        const reused = ep === true || (!!ep && typeof ep === 'object' && (!result.loadId || ep.loadId === result.loadId));
        const loadId = result.loadId || (ep && typeof ep === 'object' ? ep.loadId : null);
        return (
            <>
                <Typography sx={{ color: reused ? THEME.amber : THEME.mint, fontWeight: 600, mb: 1 }}>
                    {reused
                        ? 'org-backend found this transfer already open and created nothing new.'
                        : 'Pending bank transfer of ' + amountText + ' created for ' + orgName + '. Nothing is credited until their money arrives at Stripe.'}
                </Typography>
                {/* org-backend's Owner email is the one official copy of the bank details; Copy for email stays as a backup. */}
                <Typography sx={{ color: result.emailedTo ? THEME.mint : THEME.amber, fontSize: '0.9rem', mb: 2 }}>
                    {result.emailedTo ? 'Bank details emailed to ' + result.emailedTo : 'Not emailed'}
                </Typography>
                <InstructionsBox orgName={orgName} instructions={resultInstr} amountCents={amountCents} loadId={loadId} customerLine={resultLine} />
            </>
        );
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
            <DialogTitle sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2, fontWeight: 700 }}>Start a bank transfer</DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mt: 2 }}>
                    {blocked ? (
                        <RefusalLine text={availability.message || 'Not available yet: org-backend needs one route.'} />
                    ) : result ? renderResult() : (
                        <>
                            <Typography sx={{ color: THEME.textSecondary, fontSize: '0.9rem', mb: 2 }}>
                                Creates a pending load and Stripe's bank details for the customer to send their money to. Preview first; nothing is created until you press Create.
                            </Typography>
                            <RefusalLine text={error} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2, mb: 2 }}>
                                <Autocomplete
                                    options={orgs}
                                    value={form.org}
                                    onChange={(e, v) => edit({ org: v })}
                                    getOptionLabel={(o) => o.name || o.legalName || String(o._id)}
                                    isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Organisation *"
                                            InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                                            InputProps={{ ...params.InputProps, sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } }} />
                                    )}
                                />
                                <TextField label={'Amount (' + currency + ') *'} value={form.amount} onChange={(e) => edit({ amount: e.target.value })}
                                    placeholder="15000" error={!!form.amount && amountCents === null}
                                    helperText={form.amount && amountCents === null ? 'Dollars with at most two decimals' : ' '} {...fieldSx} />
                            </Box>
                            <RefusalLine text={form.org ? currencyRefusal : null} />
                            {preview && existing && renderExisting()}
                            {preview && !existing && (
                                <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2 }}>
                                    <Typography sx={{ color: THEME.textPrimary }}>
                                        This creates a pending bank transfer of {amountText} for {orgName}. Nothing is credited until their money arrives at Stripe.
                                    </Typography>
                                    {preview.mode && <Row label="Stripe mode" value={preview.mode} />}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                {result || blocked ? (
                    <Button onClick={close} sx={{ color: THEME.mint, textTransform: 'none', fontWeight: 600 }}>Done</Button>
                ) : (
                    <>
                        <Button onClick={close} disabled={busy} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Close</Button>
                        {preview && !existing && (
                            <Button onClick={create} disabled={busy} variant="contained" sx={primaryBtnSx}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Creating...' : 'Create'}
                            </Button>
                        )}
                        {!preview && (
                            <Button onClick={runPreview} disabled={!formValid || busy || availability.loading} variant="contained" sx={primaryBtnSx}
                                startIcon={busy ? <CircularProgress size={16} /> : null}>
                                {busy ? 'Checking...' : 'Preview'}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function MoneyPage() {
    const [loads, setLoads] = useState([]);
    const [meta, setMeta] = useState({ count: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [orgFilter, setOrgFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [orgs, setOrgs] = useState([]);
    const [orgsError, setOrgsError] = useState(null);
    const [walletsByOrg, setWalletsByOrg] = useState({});
    const [walletsUnread, setWalletsUnread] = useState(null);
    const [cancelLoad, setCancelLoad] = useState(null);
    const [recordOpen, setRecordOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const transferAvailability = useBankTransferAvailability();

    // The organisations for the filter and the form, and each org wallet's currency.
    useEffect(() => {
        (async () => {
            try {
                const headers = await authHeader();
                const res = await axios.get(`${T6_BASE}/api/admin/orgs`, { headers });
                const rows = Array.isArray(res.data) ? res.data : (res.data?.orgs ?? []);
                setOrgs([...rows].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
            } catch (err) {
                console.error('[Money] organisations unread', err.response?.status, err.response?.data || err.message);
                setOrgsError('The organisation list could not be read, so the filter and the form have no organisations. ' + refusalLine(err));
            }
            try {
                const data = await money('GET', '/org-wallets');
                const by = {};
                for (const w of data.wallets || []) if (w.orgId && !by[w.orgId]) by[w.orgId] = w;
                setWalletsByOrg(by);
            } catch (err) {
                console.error('[Money] org wallets unread', err.response?.status, err.response?.data || err.message);
                setWalletsUnread('Wallet currencies could not be read (' + refusalLine(err) + '); type the currency and org-backend checks it.');
            }
        })();
    }, []);

    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const params = { limit: 500 };
                if (orgFilter) params.orgId = String(orgFilter._id);
                if (statusFilter) params.status = statusFilter;
                const data = await money('GET', '/wallet-loads', { params });
                if (!live) return;
                setLoads(data.loads || []);
                setMeta({ count: data.count ?? 0, total: data.total ?? 0 });
            } catch (err) {
                if (!live) return;
                console.error('[Money] wallet loads unread', err.response?.status, err.response?.data || err.message);
                setLoads([]);
                setError(refusalLine(err));
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, [orgFilter, statusFilter, reloadKey]);

    const reload = () => setReloadKey((k) => k + 1);
    const pendingTotal = useMemo(() => loads.filter((l) => l.status === 'pending').length, [loads]);

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AccountBalanceWalletIcon sx={{ color: THEME.mint, fontSize: 30 }} />
                    <Box>
                        <Typography sx={{ fontSize: '1.8rem', color: THEME.textPrimary, fontWeight: 'bold' }}>Money</Typography>
                        <Typography sx={{ color: THEME.textMuted, fontSize: '0.9rem' }}>Every wallet load, newest first</Typography>
                        <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem', fontStyle: 'italic' }}>Wallets only go up when the customer's money arrives.</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: 320 }}>
                        <Button startIcon={<AccountBalanceIcon />} onClick={() => setTransferOpen(true)} variant="outlined"
                            disabled={transferAvailability.loading || transferAvailability.available === false}
                            sx={{ color: THEME.mint, borderColor: THEME.mint, fontWeight: 700, textTransform: 'none', borderRadius: '8px', px: 2.5, py: 1.2 }}>
                            Start a bank transfer
                        </Button>
                        {transferAvailability.available === false && (
                            <Typography sx={{ color: THEME.amber, fontSize: '0.75rem', mt: 0.5, textAlign: 'right' }}>{transferAvailability.message}</Typography>
                        )}
                    </Box>
                    <Button startIcon={<AddIcon />} onClick={() => setRecordOpen(true)} variant="contained" sx={{ ...primaryBtnSx, borderRadius: '8px', px: 2.5, py: 1.2 }}>
                        Record a check or wire
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <Autocomplete
                    sx={{ width: 320 }}
                    size="small"
                    options={orgs}
                    value={orgFilter}
                    onChange={(e, v) => setOrgFilter(v)}
                    getOptionLabel={(o) => o.name || o.legalName || String(o._id)}
                    isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                    renderInput={(params) => (
                        <TextField {...params} label="Organisation" placeholder="All organisations"
                            InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                            InputProps={{ ...params.InputProps, sx: { color: THEME.textPrimary, bgcolor: THEME.card, borderRadius: '8px' } }} />
                    )}
                />
                <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ width: 180 }} InputLabelProps={{ sx: { color: THEME.textMuted } }}
                    InputProps={{ sx: { color: THEME.textPrimary, bgcolor: THEME.card, borderRadius: '8px' } }}>
                    <MenuItem value="">All statuses</MenuItem>
                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                {!loading && !error && (
                    <Typography sx={{ color: THEME.textMuted, fontSize: '0.85rem' }}>
                        {meta.count < meta.total ? `Showing the newest ${meta.count} of ${meta.total}` : `${meta.total} load${meta.total === 1 ? '' : 's'}`}
                        {pendingTotal ? `, ${pendingTotal} pending` : ''}
                    </Typography>
                )}
            </Box>

            <RefusalLine text={orgsError} />

            <TableContainer component={Paper} sx={{ bgcolor: THEME.card, borderRadius: '12px' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: THEME.mint }} /></Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6, px: 2 }}>
                        <Alert severity="error" sx={{ bgcolor: THEME.cardRaised, color: THEME.pink }}>{error}</Alert>
                        <Button variant="outlined" onClick={reload} sx={{ color: THEME.mint, borderColor: THEME.mint, textTransform: 'none' }}>Retry</Button>
                    </Box>
                ) : loads.length === 0 ? (
                    <Typography sx={{ color: THEME.textMuted, textAlign: 'center', py: 6 }}>No wallet loads match these filters.</Typography>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <HeadCell>Organisation</HeadCell>
                                <HeadCell align="right">Amount</HeadCell>
                                <HeadCell>Method</HeadCell>
                                <HeadCell>Status</HeadCell>
                                <HeadCell>Reference</HeadCell>
                                <HeadCell>Created</HeadCell>
                                <HeadCell>Settled</HeadCell>
                                <HeadCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loads.map((l) => {
                                const [fg, bg] = STATUS_COLOR[l.status] || [THEME.textSecondary, THEME.cardRaised];
                                // A pending Stripe bank transfer; a card load is not cancelled here.
                                const canCancel = l.status === 'pending' && isStripeRow(l) && l.source !== 'stripe_card';
                                return (
                                    <TableRow key={l.loadId} sx={{ '&:hover': { bgcolor: THEME.mintFaint } }}>
                                        <Cell>
                                            <Typography sx={{ color: THEME.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}>{l.orgName || 'unknown organisation'}</Typography>
                                            <Typography sx={{ color: THEME.textFaint, fontSize: '0.7rem', fontFamily: 'monospace' }}>{l.loadId}</Typography>
                                        </Cell>
                                        <Cell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(l.amountCents, l.currency)}</Cell>
                                        <Cell>{methodLabel(l)}</Cell>
                                        <Cell>
                                            <Chip size="small" label={l.status || 'unknown'} sx={{ bgcolor: bg, color: fg, fontSize: '0.75rem' }} />
                                            {l.heldForReview && <Chip size="small" label="held for review" sx={{ ml: 0.5, bgcolor: THEME.amberDim, color: THEME.amber, fontSize: '0.7rem' }} />}
                                            {l.disputed && <Chip size="small" label={'disputed ' + (l.disputed.status || '')} sx={{ ml: 0.5, bgcolor: THEME.cardRaised, color: THEME.pink, fontSize: '0.7rem' }} />}
                                            {l.status === 'cancelled' && l.cancelReason && (
                                                <Typography sx={{ color: THEME.textMuted, fontSize: '0.7rem', mt: 0.5 }}>{l.cancelReason}</Typography>
                                            )}
                                        </Cell>
                                        <Cell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{l.reference || l.bankTransferReference || '—'}</Cell>
                                        <Cell sx={{ color: THEME.textSecondary }}>{fmtDate(l.createdAt)}</Cell>
                                        <Cell sx={{ color: THEME.textSecondary }}>{fmtDate(l.settledAt)}</Cell>
                                        <Cell align="right">
                                            {canCancel && (
                                                <Button size="small" onClick={() => setCancelLoad(l)}
                                                    sx={{ color: THEME.pink, border: `1px solid ${THEME.pink}`, textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
                                                    Cancel
                                                </Button>
                                            )}
                                        </Cell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <CancelLoadDialog
                load={cancelLoad}
                onClose={() => setCancelLoad(null)}
                onDone={() => { setCancelLoad(null); reload(); }}
            />
            <RecordDepositDialog
                open={recordOpen}
                orgs={orgs}
                walletsByOrg={walletsByOrg}
                walletsUnread={walletsUnread}
                onClose={() => setRecordOpen(false)}
                onCredited={reload}
            />
            <StartBankTransferDialog
                open={transferOpen}
                orgs={orgs}
                initialOrg={orgFilter}
                availability={transferAvailability}
                onClose={() => setTransferOpen(false)}
                onCreated={reload}
            />
        </PageContainer>
    );
}
